import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { queue } from '@nullvoxpopuli/ember-composable-helpers';
import set from 'ember-set-helper/helpers/set';
import {
  HdsTable,
  HdsLinkInline,
  HdsIcon,
  HdsBadge,
  HdsDropdown,
} from '@hashicorp/design-system-components/components';
import { Flex } from 'core/components';
import {
  optionForSourceState,
  resourceIsBusy,
  resourceTypeFor,
} from 'core/helpers';
import { TYPE_NETWORK_PEERING } from 'common/utils/cloud-resource-types';
import networksConnectionSourceType from 'networks-common/helpers/networks-connection-source-type';
import peeringDestinationInfo from 'hcp/helpers/peering-destination-info';
import DeletePeeringModal from 'hcp/components/page/hvn-peerings/delete-peering-modal';
import ZeroState from 'hcp/components/page/hvn-peerings/list/zero-state';

/**
 *
 * `model` will be a model containing the peerings list, the current organization, and project.
 * @argument model;
 * @type {Object}
 *
 */
/**
 *
 * `PageHvnPeeringsList` renders the AWS HVN Peerings List page.
 *
 *
 * ```
 * <Page::HvnPeerings::List::Aws
 *   @model={{model}}
 * />
 * ```
 *
 * @class PageHvnPeeringsListAws
 *
 */
export default class PageHvnPeeringsListAws extends Component {
  @service api;
  @service operation;

  @tracked showDeleteModal = false;
  @tracked peeringToDelete;
  get peeringType() {
    return TYPE_NETWORK_PEERING;
  }
  <template>
    {{#if @model.peerings.length}}
      <HdsTable
        @valign="middle"
        @caption={{t "components.page.hvn-peerings.list.title"}}
      >
        <:head as |H|>
          <H.Tr>
            <H.Th>
              {{t
                "components.page.hvn-peerings.list.table.headers.connection-id"
              }}
            </H.Th>
            <H.Th>
              {{t "components.page.hvn-peerings.list.table.headers.status"}}
            </H.Th>
            <H.Th>
              {{t
                "components.page.hvn-peerings.list.aws.table.headers.account-id"
              }}
            </H.Th>
            <H.Th>
              {{t "components.page.hvn-peerings.list.aws.table.headers.vpc-id"}}
            </H.Th>
            <H.Th>
              {{t "components.page.hvn-peerings.list.table.headers.region"}}
            </H.Th>
            <H.Th />
          </H.Tr>
        </:head>
        <:body as |B|>
          {{#each @model.peerings key="id" as |peering|}}
            <B.Tr data-test-aws-peering-item>
              <B.Td>
                <Flex @direction="row" @gap="2xs">
                  <HdsLinkInline
                    class="hds-font-weight-medium"
                    @color="secondary"
                    @models={{array peering.id}}
                    @route="cloud.orgs.detail.projects.detail.hvns.detail.peerings.detail"
                  >
                    {{peering.id}}
                  </HdsLinkInline>
                  {{#if peering.isLocked}}
                    <HdsIcon
                      @name="lock-fill"
                      @color="var(--token-color-foreground-strong)"
                      @isInline={{true}}
                      data-test-peering-locked
                    />
                  {{/if}}
                </Flex>
              </B.Td>
              <B.Td>
                {{#let
                  (optionForSourceState
                    (networksConnectionSourceType peering) peering.state
                  )
                  as |option|
                }}
                  <HdsBadge
                    @text={{t option.text}}
                    @color={{option.color}}
                    @icon={{option.icon}}
                    @isIconOnly={{option.isIconOnly}}
                    @size={{option.size}}
                    @type={{option.type}}
                  />
                {{/let}}
              </B.Td>
              {{#let (peeringDestinationInfo peering) as |destination|}}
                <B.Td>
                  {{#let
                    (optionForSourceState "region" destination.provider)
                    as |option|
                  }}
                    <Flex @alignItems="center" @direction="row" @gap="2xs">
                      <HdsIcon
                        @name={{option.icon}}
                        @isInline={{true}}
                        data-test-aws-provider-logo
                      />
                      <span>
                        {{destination.accountId}}
                      </span>
                    </Flex>
                  {{/let}}
                </B.Td>
                <B.Td>
                  {{destination.vpc}}
                </B.Td>
                <B.Td>
                  {{destination.region}}
                </B.Td>
              {{/let}}

              <B.Td>
                {{#unless peering.isLocked}}
                  {{#unless
                    (resourceIsBusy
                      peering type=(resourceTypeFor "network-peering")
                    )
                  }}
                    <HdsDropdown as |dd|>
                      <dd.ToggleIcon
                        @icon="more-horizontal"
                        @text={{t "components.form.more"}}
                        @hasChevron={{false}}
                        data-test-more-menu-trigger
                      />
                      <dd.Interactive
                        @color="critical"
                        @icon="trash"
                        @text={{t "components.form.delete"}}
                        id="modal-dialog-open-button-{{peering.id}}"
                        data-test-delete-button
                        {{on
                          "click"
                          (queue
                            (set this "peeringToDelete" peering)
                            (set this "showDeleteModal" true)
                          )
                        }}
                      />
                    </HdsDropdown>
                  {{/unless}}
                {{/unless}}
              </B.Td>
            </B.Tr>
          {{/each}}
        </:body>
      </HdsTable>
      <DeletePeeringModal
        @model={{@model}}
        @peering={{this.peeringToDelete}}
        @isOpen={{this.showDeleteModal}}
        @openStateChanged={{set this "showDeleteModal"}}
      />
    {{else}}
      <ZeroState @provider="aws" />
    {{/if}}
  </template>
}
